import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Clear,
  AttachMoney,
  CalendarToday,
  Business,
  Add,
  Delete,
  Receipt,
  Person
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import invoicesService from '../services/invoicesService';
import projectsService, { Project } from '../services/projectsService';
import clientPortalService, { Client } from '../services/clientPortalService';
import api from '../services/api';
import companyService from '../services/companyService';

interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
  total: number;
}

interface InvoiceFormData {
  invoiceNumber: string;
  projectId: string;
  clientId: string;
  companyId?: string;
  invoiceDate: string;
  dueDate: string;
  status: string;
  paymentTerms: string;
  taxRate: string;
  discountAmount: string;
  notes: string;
  lineItems: InvoiceLineItem[];
}

const InvoiceCreate: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<InvoiceFormData>({
    invoiceNumber: `INV-${Date.now()}`,
    projectId: '',
    clientId: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    status: 'Draft',
    paymentTerms: 'Net 30',
    taxRate: '0',
    discountAmount: '0',
    notes: '',
    lineItems: []
  });

  const [errors, setErrors] = useState<Partial<InvoiceFormData>>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);

  const statusOptions = [
    'Draft',
    'Sent',
    'Viewed',
    'Paid',
    'Overdue',
    'Cancelled'
  ];

  const paymentTermsOptions = [
    'Due on Receipt',
    'Net 15',
    'Net 30',
    'Net 45',
    'Net 60',
    '2/10 Net 30',
    'Custom'
  ];

  const [projectsList, setProjectsList] = useState<Project[]>([]);
  const [clientsList, setClientsList] = useState<Client[]>([]);
  const [companiesList, setCompaniesList] = useState<{ id: string; name: string }[]>([]);
  const [showCompanySelect, setShowCompanySelect] = useState(false);
  const [currentCompanyName, setCurrentCompanyName] = useState<string>('');
  const [loadingLists, setLoadingLists] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLists = async () => {
      setLoadingLists(true);
      setFetchError(null);
      try {
        // Determine user role and default company
        let shouldSelectCompany = false;
        try {
          const me = await api.get('/auth/me');
          const user = me?.data?.user || me?.data;
          const roleRaw = String(user?.role || '').toLowerCase();
          const isAdminRole = roleRaw.includes('admin');
          const company = user?.company;
          const companyId = typeof company === 'object' ? (company?._id || company?.id) : company;
          const companyName = typeof company === 'object' ? (company?.name || company?.legalName) : '';
          setCurrentCompanyName(companyName || '');
          if (companyId) {
            setFormData(prev => ({ ...prev, companyId: String(companyId) }));
          }
          // Admins (any admin variant) or users without company should select company
          shouldSelectCompany = isAdminRole || !companyId;
          setShowCompanySelect(shouldSelectCompany);
        } catch (e) {
          // If /auth/me fails, show selector to prevent blocking submit
          shouldSelectCompany = true;
          setShowCompanySelect(true);
        }

        // Fetch projects using existing backend shape { success, data, pagination }
        const projRes = await projectsService.getRecent(50);
        const fetchedProjects: Project[] = Array.isArray((projRes as any)?.data?.data)
          ? (projRes as any).data.data
          : Array.isArray((projRes as any)?.data)
            ? (projRes as any).data
            : [];

        // Attempt to fetch clients; if unavailable, derive from projects' embedded client info
        let fetchedClients: Client[] = [];
        try {
          const clientRes = await clientPortalService.getClients();
          fetchedClients = Array.isArray(clientRes)
            ? clientRes
            : Array.isArray((clientRes as any)?.data)
              ? (clientRes as any).data
              : Array.isArray((clientRes as any)?.data?.data)
                ? (clientRes as any).data.data
                : [];
        } catch (e) {
          // No clients endpoint or error; build a minimal list from projects
          const seen = new Set<string>();
          fetchedClients = fetchedProjects
            .map((p) => {
              const name = (p as any)?.client?.name || 'Unknown Client';
              if (!name || seen.has(name)) return null;
              seen.add(name);
              const email = (p as any)?.client?.email || '';
              const [firstName, ...rest] = name.split(' ');
              const lastName = rest.join(' ');
              return {
                _id: name,
                companyName: name,
                contactPerson: {
                  firstName: firstName || '',
                  lastName: lastName || '',
                  email,
                  phone: (p as any)?.client?.phone || '',
                  title: ''
                },
                address: {
                  street: (p as any)?.client?.address?.street || '',
                  city: (p as any)?.client?.address?.city || '',
                  state: (p as any)?.client?.address?.state || '',
                  zipCode: (p as any)?.client?.address?.zipCode || '',
                  country: (p as any)?.client?.address?.country || 'USA'
                },
                clientType: 'business',
                status: 'active',
                portalAccess: { enabled: false, permissions: [] },
                projects: [],
                documents: [],
                communications: [],
                preferences: {
                  notifications: { email: true, sms: false, projectUpdates: true, invoiceReminders: true, documentSharing: true },
                  communication: { preferredMethod: 'email', timezone: 'UTC', language: 'en' },
                  portal: { theme: 'light', dashboardLayout: '', defaultView: 'invoices' }
                },
                createdBy: { _id: '', name: '' },
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              } as Client;
            })
            .filter(Boolean) as Client[];
          setFetchError('Clients API unavailable; showing clients derived from projects.');
        }

        setProjectsList(fetchedProjects);
        setClientsList(fetchedClients);

        // Fetch companies when selector should be shown (use local flag to avoid state race)
        if (shouldSelectCompany) {
          try {
            const companies = await companyService.getAllCompanies();
            const list = Array.isArray(companies)
              ? companies.map((c: any) => ({ id: c.id || c._id, name: c.name || c.legalName || 'Company' }))
              : [];
            setCompaniesList(list);
          } catch (e) {
            // Non-blocking; user may still have company from /auth/me
          }
        }
      } catch (err: any) {
        console.error('Failed to load projects/clients', err);
        setFetchError('Failed to load projects/clients. Please try again.');
      } finally {
        setLoadingLists(false);
      }
    };
    fetchLists();
  }, []);

  const handleInputChange = (field: keyof InvoiceFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate due date when invoice date changes
    if (field === 'invoiceDate' && formData.paymentTerms) {
      const invoiceDate = new Date(value);
      let daysToAdd = 30; // Default
      
      if (formData.paymentTerms === 'Due on Receipt') daysToAdd = 0;
      else if (formData.paymentTerms === 'Net 15') daysToAdd = 15;
      else if (formData.paymentTerms === 'Net 30') daysToAdd = 30;
      else if (formData.paymentTerms === 'Net 45') daysToAdd = 45;
      else if (formData.paymentTerms === 'Net 60') daysToAdd = 60;
      else if (formData.paymentTerms === '2/10 Net 30') daysToAdd = 30;
      
      const dueDate = new Date(invoiceDate);
      dueDate.setDate(dueDate.getDate() + daysToAdd);
      
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        dueDate: dueDate.toISOString().split('T')[0]
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const addLineItem = () => {
    const newLineItem: InvoiceLineItem = {
      id: Date.now().toString(),
      description: '',
      quantity: '1',
      unitPrice: '',
      total: 0
    };
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, newLineItem]
    }));
  };

  const removeLineItem = (id: string) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter(item => item.id !== id)
    }));
  };

  const updateLineItem = (id: string, field: keyof InvoiceLineItem, value: string) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          // Calculate total when quantity or unit price changes
          if (field === 'quantity' || field === 'unitPrice') {
            const quantity = parseFloat(field === 'quantity' ? value : item.quantity) || 0;
            const unitPrice = parseFloat(field === 'unitPrice' ? value : item.unitPrice) || 0;
            updatedItem.total = quantity * unitPrice;
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const calculateSubtotal = () => {
    const lineItemsArray = Array.isArray(formData.lineItems) ? formData.lineItems : [];
    return lineItemsArray.reduce((sum, item) => sum + (item?.total || 0), 0);
  };

  const calculateTax = () => {
    const subtotal = calculateSubtotal();
    const taxRate = parseFloat(formData.taxRate) || 0;
    return subtotal * (taxRate / 100);
  };

  const calculateDiscount = () => {
    return parseFloat(formData.discountAmount) || 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax() - calculateDiscount();
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<InvoiceFormData> = {};

    if (!formData.invoiceNumber.trim()) newErrors.invoiceNumber = 'Invoice number is required';
    if (!formData.projectId) newErrors.projectId = 'Project is required';
    if (!formData.clientId) newErrors.clientId = 'Client is required';
    if (!formData.invoiceDate) newErrors.invoiceDate = 'Invoice date is required';
    if (!formData.dueDate) newErrors.dueDate = 'Due date is required';

    // Require company selection when selector is shown
    if (showCompanySelect && !formData.companyId) {
      newErrors.companyId = 'Company is required';
    }

    // Validate tax rate is a number
    if (formData.taxRate && isNaN(Number(formData.taxRate))) {
      newErrors.taxRate = 'Tax rate must be a valid number';
    }

    // Validate discount amount is a number
    if (formData.discountAmount && isNaN(Number(formData.discountAmount))) {
      newErrors.discountAmount = 'Discount amount must be a valid number';
    }

    // Validate date range
    if (formData.invoiceDate && formData.dueDate && new Date(formData.invoiceDate) > new Date(formData.dueDate)) {
      newErrors.dueDate = 'Due date must be after invoice date';
    }

    // Validate at least one line item
    if (formData.lineItems.length === 0) {
      newErrors.lineItems = 'At least one line item is required' as any;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus('error');
      // Populate a summary list for the alert based on client-side validation
      const clientSideErrors = Object.values(errors).filter(Boolean) as string[];
      setSubmitErrors(clientSideErrors.length > 0 ? clientSideErrors : ['Please review required fields.']);
      return;
    }

    setSubmitStatus('loading');
    setSubmitErrors([]);

    try {
      const selectedProject = projectsList.find(p => p._id === formData.projectId);
      const selectedClient = clientsList.find(c => c._id === formData.clientId);

      const items = (formData.lineItems || []).map(li => ({
        description: li.description,
        quantity: parseFloat(li.quantity) || 0,
        rate: parseFloat(li.unitPrice) || 0,
        amount: li.total || ((parseFloat(li.quantity) || 0) * (parseFloat(li.unitPrice) || 0))
      }));

      const payload = {
        invoiceNumber: formData.invoiceNumber,
        client: {
          name: selectedClient
            ? `${selectedClient.contactPerson?.firstName || ''} ${selectedClient.contactPerson?.lastName || ''}`.trim() || selectedClient.companyName
            : formData.clientId,
          company: selectedClient?.companyName || '',
          email: selectedClient?.contactPerson?.email || ''
        },
        project: formData.projectId,
        amount: calculateTotal(),
        taxAmount: calculateTax(),
        description: formData.notes || (selectedProject ? `Invoice for ${selectedProject.name}` : 'Invoice'),
        items,
        paymentTerms: formData.paymentTerms,
        dueDate: formData.dueDate,
        issueDate: formData.invoiceDate,
        notes: formData.notes,
        projectId: formData.projectId,
        clientId: formData.clientId,
        companyId: formData.companyId,
      } as const;

      await invoicesService.createInvoice(payload as any);

      setSubmitStatus('success');
      navigate('/invoices');
    } catch (error) {
      console.error('Error creating invoice:', error);
      // Extract server-side validation details if available
      let serverErrors: string[] = [];
      const anyErr = error as any;
      if (anyErr && anyErr.response && anyErr.response.data) {
        const data = anyErr.response.data;
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          serverErrors = data.errors.map((e: any) => (typeof e === 'string' ? e : e?.message || String(e))).filter(Boolean);
        } else if (typeof data.message === 'string' && data.message.trim()) {
          serverErrors = [data.message];
        }
      }
      if (serverErrors.length === 0) {
        serverErrors = ['Failed to create invoice.'];
      }
      setSubmitErrors(serverErrors);
      // Surface the first message in the alert header
      setFetchError(serverErrors[0]);
      setSubmitStatus('error');
    }
  };

  const handleReset = () => {
    setFormData({
      invoiceNumber: `INV-${Date.now()}`,
      projectId: '',
      clientId: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      status: 'Draft',
      paymentTerms: 'Net 30',
      taxRate: '0',
      discountAmount: '0',
      notes: '',
      lineItems: []
    });
    setErrors({});
    setSubmitStatus('idle');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'loading': return 'info';
      default: return 'default';
    }
  };

  const getInvoiceStatusColor = (status: string) => {
    switch (status) {
      case 'Paid': return 'success';
      case 'Sent': case 'Viewed': return 'info';
      case 'Overdue': return 'error';
      case 'Cancelled': return 'default';
      default: return 'warning';
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/invoices')} 
            sx={{ color: 'white' }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Create New Invoice
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Generate a professional invoice for your construction services
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Status Alert */}
      {submitStatus !== 'idle' && (
        <Alert 
          severity={getStatusColor(submitStatus) as any}
          sx={{ mb: 3 }}
          onClose={() => setSubmitStatus('idle')}
        >
          {submitStatus === 'loading' && 'Creating invoice...'}
          {submitStatus === 'success' && 'Invoice created successfully! Redirecting...'}
          {submitStatus === 'error' && (
            <Box>
              <Typography variant="body2" sx={{ mb: submitErrors.length ? 1 : 0 }}>
                {fetchError || 'Please fix the errors below and try again.'}
              </Typography>
              {submitErrors.length > 0 && (
                <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3 }}>
                  {submitErrors.map((msg, idx) => (
                    <li key={idx}>
                      <Typography variant="caption" color="error">
                        {msg}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Alert>
      )}

      {/* Data Fetch Alert */}
      {submitStatus === 'idle' && fetchError && (
        <Alert 
          severity="warning"
          sx={{ mb: 3 }}
          onClose={() => setFetchError(null)}
        >
          {fetchError}
        </Alert>
      )}

      {/* Form */}
      <Card elevation={2}>
        <form onSubmit={handleSubmit}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'grid', gap: 4 }}>
              {/* Invoice Details */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Invoice Details
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <TextField
                    fullWidth
                    label="Invoice Number"
                    value={formData.invoiceNumber}
                    onChange={handleInputChange('invoiceNumber')}
                    error={!!errors.invoiceNumber}
                    helperText={errors.invoiceNumber}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Receipt color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      label="Status"
                    >
                      {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          <Chip 
                            label={status} 
                            size="small" 
                            color={getInvoiceStatusColor(status)}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Invoice Date"
                    type="date"
                    value={formData.invoiceDate}
                    onChange={handleInputChange('invoiceDate')}
                    error={!!errors.invoiceDate}
                    helperText={errors.invoiceDate}
                    required
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarToday color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Due Date"
                    type="date"
                    value={formData.dueDate}
                    onChange={handleInputChange('dueDate')}
                    error={!!errors.dueDate}
                    helperText={errors.dueDate}
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>

              {/* Client & Project Information */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Client & Project Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <FormControl fullWidth error={!!errors.projectId}>
                    <InputLabel>Project *</InputLabel>
                    <Select
                      value={formData.projectId}
                      onChange={(e) => setFormData(prev => ({ ...prev, projectId: e.target.value }))}
                      label="Project *"
                    >
                      {loadingLists && (
                        <MenuItem disabled value="">
                          Loading projects...
                        </MenuItem>
                      )}
                      {!loadingLists && projectsList.length === 0 && (
                        <MenuItem disabled value="">
                          No projects found
                        </MenuItem>
                      )}
                      {!loadingLists && projectsList.map((project) => (
                        <MenuItem key={project._id} value={project._id}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.projectId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.projectId}
                      </Typography>
                    )}
                  </FormControl>

                  <FormControl fullWidth error={!!errors.clientId}>
                    <InputLabel>Client *</InputLabel>
                    <Select
                      value={formData.clientId}
                      onChange={(e) => setFormData(prev => ({ ...prev, clientId: e.target.value }))}
                      label="Client *"
                    >
                      {loadingLists && (
                        <MenuItem disabled value="">
                          Loading clients...
                        </MenuItem>
                      )}
                      {!loadingLists && clientsList.length === 0 && (
                        <MenuItem disabled value="">
                          No clients found
                        </MenuItem>
                      )}
                      {!loadingLists && clientsList.map((client) => (
                        <MenuItem key={client._id} value={client._id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person fontSize="small" />
                            {client.companyName}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.clientId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.clientId}
                      </Typography>
                    )}
                  </FormControl>

                  {/* Company Selection (admins or users without associated company) */}
                  {showCompanySelect ? (
                    <FormControl fullWidth error={!!errors.companyId}>
                      <InputLabel>Company *</InputLabel>
                      <Select
                        value={formData.companyId || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, companyId: String(e.target.value) }))}
                        label="Company *"
                      >
                        {loadingLists && (
                          <MenuItem disabled value="">
                            Loading companies...
                          </MenuItem>
                        )}
                        {!loadingLists && companiesList.length === 0 && (
                          <MenuItem disabled value="">
                            No companies found
                          </MenuItem>
                        )}
                        {!loadingLists && companiesList.map((company) => (
                          <MenuItem key={company.id} value={company.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Business fontSize="small" />
                              {company.name}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.companyId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                          {errors.companyId as any}
                        </Typography>
                      )}
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      label="Company"
                      value={currentCompanyName || ''}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Business color="action" />
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                    />
                  )}

                  <FormControl fullWidth>
                    <InputLabel>Payment Terms</InputLabel>
                    <Select
                      value={formData.paymentTerms}
                      onChange={(e) => {
                        const newTerms = e.target.value;
                        setFormData(prev => ({ ...prev, paymentTerms: newTerms }));
                        
                        // Auto-update due date based on payment terms
                        if (formData.invoiceDate) {
                          const invoiceDate = new Date(formData.invoiceDate);
                          let daysToAdd = 30;
                          
                          if (newTerms === 'Due on Receipt') daysToAdd = 0;
                          else if (newTerms === 'Net 15') daysToAdd = 15;
                          else if (newTerms === 'Net 30') daysToAdd = 30;
                          else if (newTerms === 'Net 45') daysToAdd = 45;
                          else if (newTerms === 'Net 60') daysToAdd = 60;
                          else if (newTerms === '2/10 Net 30') daysToAdd = 30;
                          
                          const dueDate = new Date(invoiceDate);
                          dueDate.setDate(dueDate.getDate() + daysToAdd);
                          
                          setFormData(prev => ({ 
                            ...prev, 
                            paymentTerms: newTerms,
                            dueDate: dueDate.toISOString().split('T')[0]
                          }));
                        }
                      }}
                      label="Payment Terms"
                    >
                      {paymentTermsOptions.map((term) => (
                        <MenuItem key={term} value={term}>
                          {term}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Line Items */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" color="primary">
                    Invoice Items
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addLineItem}
                  >
                    Add Item
                  </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />

                {formData.lineItems.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.lineItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Autocomplete
                                freeSolo
                                options={[]}
                                value={item.description}
                                onChange={(_, newValue) => {
                                  updateLineItem(item.id, 'description', newValue || '');
                                }}
                                onInputChange={(_, newInputValue) => {
                                  updateLineItem(item.id, 'description', newInputValue);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Service or item description"
                                    sx={{ minWidth: 300 }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateLineItem(item.id, 'quantity', e.target.value)}
                                sx={{ width: 80 }}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={item.unitPrice}
                                onChange={(e) => updateLineItem(item.id, 'unitPrice', e.target.value)}
                                sx={{ width: 100 }}
                                inputProps={{ min: 0, step: 0.01 }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="medium">
                                ${item.total.toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeLineItem(item.id)}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography variant="body1" color="text.secondary">
                      No items added yet. Click "Add Item" to get started.
                    </Typography>
                  </Paper>
                )}

                {/* Invoice Summary */}
                {formData.lineItems.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Paper variant="outlined" sx={{ p: 3, bgcolor: 'grey.50' }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 300px' }, gap: 3 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                          <TextField
                            label="Tax Rate (%)"
                            type="number"
                            value={formData.taxRate}
                            onChange={handleInputChange('taxRate')}
                            error={!!errors.taxRate}
                            helperText={errors.taxRate}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                          />
                          <TextField
                            label="Discount Amount"
                            type="number"
                            value={formData.discountAmount}
                            onChange={handleInputChange('discountAmount')}
                            error={!!errors.discountAmount}
                            helperText={errors.discountAmount}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                          />
                        </Box>
                        
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography>Subtotal:</Typography>
                            <Typography>${calculateSubtotal().toFixed(2)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography>Tax ({formData.taxRate}%):</Typography>
                            <Typography>${calculateTax().toFixed(2)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography>Discount:</Typography>
                            <Typography>-${calculateDiscount().toFixed(2)}</Typography>
                          </Box>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="h6" fontWeight="bold">Total:</Typography>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                              ${calculateTotal().toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                )}
              </Box>

              {/* Notes */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Additional Notes
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange('notes')}
                  multiline
                  rows={4}
                  placeholder="Payment instructions, terms and conditions, or other notes..."
                />
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<Clear />}
                  disabled={submitStatus === 'loading'}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={submitStatus === 'loading'}
                >
                  {submitStatus === 'loading' ? 'Creating...' : 'Create Invoice'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </form>
      </Card>
    </Box>
  );
};

export default InvoiceCreate;